import { useSyncExternalStore, type MouseEvent } from 'react';
import { NoticeProvider } from './components/notices';
import { RepoContext } from './context';
import type { TournamentRepo } from './lib/repo';
import { parseHash, useHash } from './lib/router';
import { ListPage } from './pages/ListPage';
import { NewTournamentPage } from './pages/NewTournamentPage';
import { TournamentPage } from './pages/TournamentPage';

function subscribeOnline(onChange: () => void): () => void {
  window.addEventListener('online', onChange);
  window.addEventListener('offline', onChange);
  return () => {
    window.removeEventListener('online', onChange);
    window.removeEventListener('offline', onChange);
  };
}

function useOnline(): boolean {
  return useSyncExternalStore(
    subscribeOnline,
    () => navigator.onLine,
    () => true,
  );
}

function skipToContent(e: MouseEvent<HTMLAnchorElement>): void {
  // Hash links are routes here, so move focus by hand instead of following "#main".
  e.preventDefault();
  document.getElementById('main')?.focus();
}

export function App({ repo }: { repo: TournamentRepo }) {
  const route = parseHash(useHash());
  const online = useOnline();
  return (
    <RepoContext.Provider value={repo}>
      <NoticeProvider>
        <a className="skip-link" href="#main" onClick={skipToContent}>
          Skip to content
        </a>
        <header className="app-header no-print">
          <a className="brand" href="#/">
            <img src="./icon.svg" alt="" width="28" height="28" />
            <span>Chess TD</span>
          </a>
          {!online && (
            <span className="badge badge-offline" role="status">
              Offline: changes are saved on this device
            </span>
          )}
        </header>
        <main id="main" tabIndex={-1}>
          {route.name === 'list' && <ListPage />}
          {route.name === 'new' && <NewTournamentPage />}
          {route.name === 'tournament' && (
            <TournamentPage key={route.id} id={route.id} tab={route.tab} />
          )}
        </main>
        <footer className="app-footer no-print">
          <p>
            Chess Tournament Director · open source (MIT) · works offline; tournaments are stored in
            this browser. Export them to keep a copy.
          </p>
        </footer>
      </NoticeProvider>
    </RepoContext.Provider>
  );
}
